import Pjax from "pjax"
import { Modal } from "../../submodules/rapdv/client/elements/Bootstrap"
import { ClientPage } from "../../submodules/rapdv/client/elements/PagesCtrl"
import { Basket, BasketAddon, BasketItem, DishFeeType, Menu, MenuAddon, MenuData } from "../elements/Menu"
import { Translation } from "../elements/Translation"

export class PageRestaurant implements ClientPage {

  private static readonly TRANSLATION_ID = "PageRestaurant"

  private menu: Menu
  private menuData: MenuData
  private basket: Basket
  private itemsEl: HTMLElement | null
  private emptyEl: HTMLElement | null
  private pjax: Pjax
  private addonModal: Modal | null = null
  private pendingAddonDishId: string | null = null
  private pendingAddonHalves: [string, string] | null = null
  private translation = new Translation()
  private t = (key: string): string => this.translation.t(key)

  getPageId = (): string => "page-restaurant"

  execute = async (pjax: Pjax): Promise<void> => {
    this.pjax = pjax

    const dataEl = document.getElementById("menu-basket-data")
    if (!dataEl) return

    try {
      this.menuData = JSON.parse(dataEl.textContent || "{}")
    } catch (error) {
      return
    }
    if (!this.menuData?.dishes) return

    await this.translation.fetchTranslations(PageRestaurant.TRANSLATION_ID)

    this.itemsEl = document.querySelector("[data-basket-items]")
    this.emptyEl = document.querySelector("[data-basket-empty]")

    this.menu = new Menu(this.menuData)
    this.basket = this.menu.load()

    this.setupAddButtons()
    this.setupHalfAndHalf()
    this.setupAddonModal()
    this.render()
  }

  onPageClose = (): void => {}

  private setupAddButtons = (): void => {
    const buttons = document.querySelectorAll("[data-basket-add]")
    for (const button of buttons) {
      button.addEventListener("click", () => this.addDish((button as HTMLElement).dataset.dishId))
    }
  }

  private addDish = (dishId: string | undefined): void => {
    if (!dishId) return
    if (this.menu.dishHasAddons(dishId)) {
      this.openAddonModal(dishId)
      return
    }
    this.addDishWithAddons(dishId, [])
  }

  private addDishWithAddons = (dishId: string, addons: BasketAddon[]): void => {
    const newItem: BasketItem = { dishId, count: 1, ...(addons.length > 0 && { addons }) }
    const key = this.menu.getItemKey(newItem)

    const existing = this.basket.items.find((item) => this.menu.getItemKey(item) === key)
    if (existing) {
      existing.count += 1
    } else {
      this.basket.items.push(newItem)
    }

    this.menu.save(this.basket)
    this.render()
  }

  private setupAddonModal = (): void => {
    const modalEl = document.querySelector<HTMLElement>("[data-addon-modal]")
    if (!modalEl) return
    this.addonModal = Modal.getOrCreateInstance(modalEl)

    for (const cancelButton of modalEl.querySelectorAll("[data-addon-cancel]")) {
      cancelButton.addEventListener("click", () => this.addonModal?.hide())
    }
    modalEl.querySelector("[data-addon-confirm]")?.addEventListener("click", () => this.confirmAddons())
  }

  private openAddonModal = (dishId: string): void => {
    const modalEl = document.querySelector<HTMLElement>("[data-addon-modal]")
    if (!modalEl) {
      this.addDishWithAddons(dishId, [])
      return
    }
    this.pendingAddonDishId = dishId
    this.pendingAddonHalves = null
    this.populateAddonSection(modalEl, "dish", this.menu.getDishAddons(dishId))
    this.populateAddonSection(modalEl, "category", this.menu.getCategoryAddons(dishId))
    this.addonModal?.show()
  }

  private openAddonModalForHalves = (firstDishId: string, secondDishId: string): void => {
    const modalEl = document.querySelector<HTMLElement>("[data-addon-modal]")
    if (!modalEl) {
      this.addHalfAndHalf(firstDishId, secondDishId, [])
      return
    }
    this.pendingAddonDishId = null
    this.pendingAddonHalves = [firstDishId, secondDishId]
    this.populateAddonSection(modalEl, "dish", this.menu.getHalfAndHalfDishAddons(firstDishId, secondDishId))
    this.populateAddonSection(modalEl, "category", this.menu.getHalfAndHalfCategoryAddons(firstDishId, secondDishId))
    this.addonModal?.show()
  }

  private populateAddonSection = (modalEl: HTMLElement, section: string, addons: MenuAddon[]): void => {
    const sectionEl = modalEl.querySelector<HTMLElement>(`[data-addon-section="${section}"]`)
    const listEl = modalEl.querySelector<HTMLElement>(`[data-addon-list="${section}"]`)
    if (!sectionEl || !listEl) return
    sectionEl.classList.toggle("d-none", addons.length === 0)
    listEl.innerHTML = ""
    for (const addon of addons) listEl.appendChild(this.buildAddonRow(addon))
  }

  private buildAddonRow = (addon: MenuAddon): HTMLElement => {
    const rowEl = document.createElement("div")
    rowEl.className = "d-flex justify-content-between align-items-center py-1"
    rowEl.dataset.addonItem = "true"
    rowEl.dataset.addonName = addon.name
    rowEl.dataset.addonPrice = String(addon.price)
    rowEl.dataset.addonQty = "0"

    const priceLabel = addon.price > 0 ? `<span class="text-muted ms-1">+${this.menu.formatPrice(addon.price)}</span>` : ""

    rowEl.innerHTML = `
      <label class="d-flex align-items-center gap-2 mb-0" style="cursor:pointer;">
        <input type="checkbox" class="form-check-input mt-0" data-addon-check />
        <span>${addon.name}${priceLabel}</span>
      </label>
    `
    const checkbox = rowEl.querySelector<HTMLInputElement>("[data-addon-check]")
    checkbox?.addEventListener("change", () => { rowEl.dataset.addonQty = checkbox.checked ? "1" : "0" })

    return rowEl
  }

  private confirmAddons = (): void => {
    const dishId = this.pendingAddonDishId
    const halves = this.pendingAddonHalves
    this.addonModal?.hide()
    this.pendingAddonDishId = null
    this.pendingAddonHalves = null
    if (!dishId && !halves) return

    const modalEl = document.querySelector<HTMLElement>("[data-addon-modal]")
    const addons: BasketAddon[] = []
    for (const itemEl of modalEl?.querySelectorAll<HTMLElement>("[data-addon-item]") ?? []) {
      const quantity = parseInt(itemEl.dataset.addonQty ?? "0", 10)
      if (quantity <= 0) continue
      addons.push({ name: itemEl.dataset.addonName ?? "", price: Number(itemEl.dataset.addonPrice) || 0, quantity })
    }

    if (halves) {
      this.addHalfAndHalf(halves[0], halves[1], addons)
      return
    }
    this.addDishWithAddons(dishId!, addons)
  }

  private addHalfAndHalf = (firstDishId: string, secondDishId: string, addons: BasketAddon[]): void => {
    const halves = [firstDishId, secondDishId].sort() as [string, string]
    const newItem: BasketItem = { halfAndHalf: halves, count: 1, ...(addons.length > 0 && { addons }) }
    const key = this.menu.getItemKey(newItem)

    const existing = this.basket.items.find((item) => this.menu.getItemKey(item) === key)
    if (existing) {
      existing.count += 1
    } else {
      this.basket.items.push(newItem)
    }

    this.menu.save(this.basket)
    this.render()
  }

  private changeCountByKey = (itemKey: string | undefined, delta: number): void => {
    if (!itemKey) return

    const existing = this.basket.items.find((item) => this.menu.getItemKey(item) === itemKey)
    if (existing) {
      existing.count += delta
      if (existing.count <= 0) {
        this.basket.items = this.basket.items.filter((item) => this.menu.getItemKey(item) !== itemKey)
      }
    }

    this.menu.save(this.basket)
    this.render()
  }

  private setupHalfAndHalf = (): void => {
    for (const openButton of document.querySelectorAll("[data-hh-open]")) {
      openButton.addEventListener("click", () => {
        const targetId = (openButton as HTMLElement).dataset.hhTarget
        const modalEl = targetId ? document.getElementById(targetId) : null
        if (!modalEl) return
        this.clearHalfAndHalfSelection(modalEl)
        Modal.getOrCreateInstance(modalEl).show()
      })
    }

    for (const modalEl of document.querySelectorAll("[data-hh-modal]")) {
      const modal = modalEl as HTMLElement
      for (const cancelButton of modal.querySelectorAll("[data-hh-cancel]")) {
        cancelButton.addEventListener("click", () => Modal.getOrCreateInstance(modal).hide())
      }
      for (const optionButton of modal.querySelectorAll("[data-hh-option]")) {
        optionButton.addEventListener("click", () => this.onHalfAndHalfOptionClick(modal, optionButton as HTMLElement))
      }
      for (const cellEl of modal.querySelectorAll<HTMLElement>("td[data-hh-size-col]")) {
        cellEl.addEventListener("mouseenter", () => this.toggleHalfAndHalfColumnHover(modal, cellEl.dataset.hhSizeCol, true))
        cellEl.addEventListener("mouseleave", () => this.toggleHalfAndHalfColumnHover(modal, cellEl.dataset.hhSizeCol, false))
      }
      modal.addEventListener("hidden.bs.modal", () => this.clearHalfAndHalfSelection(modal))
    }
  }

  private clearHalfAndHalfSelection = (modal: HTMLElement): void => {
    for (const option of modal.querySelectorAll("[data-hh-option]")) option.classList.remove("active")
    this.updateHalfAndHalfSizeLock(modal)
  }

  private toggleHalfAndHalfColumnHover = (modal: HTMLElement, sizeIndex: string | undefined, isHovered: boolean): void => {
    if (sizeIndex === undefined) return
    for (const cellEl of modal.querySelectorAll(`[data-hh-size-col="${sizeIndex}"]`)) {
      cellEl.classList.toggle("hh-col-hover", isHovered)
    }
  }

  private updateHalfAndHalfSizeLock = (modal: HTMLElement): void => {
    const activeSize = modal.querySelector<HTMLElement>("[data-hh-option].active")?.dataset.hhSize
    for (const option of modal.querySelectorAll<HTMLButtonElement>("[data-hh-option]")) {
      if (option.dataset.hhSize === undefined) continue
      option.disabled = activeSize !== undefined && option.dataset.hhSize !== activeSize
    }
  }

  private onHalfAndHalfOptionClick = (modal: HTMLElement, optionButton: HTMLElement): void => {
    if (optionButton.classList.contains("active")) {
      optionButton.classList.remove("active")
      this.updateHalfAndHalfSizeLock(modal)
      return
    }
    optionButton.classList.add("active")

    const selected = Array.from(modal.querySelectorAll<HTMLElement>("[data-hh-option].active"))
    if (selected.length < 2) {
      this.updateHalfAndHalfSizeLock(modal)
      return
    }

    const firstDishId = selected[0].dataset.dishId
    const secondDishId = selected[1].dataset.dishId
    Modal.getOrCreateInstance(modal).hide()
    if (firstDishId && secondDishId) this.onHalfAndHalfSelected(firstDishId, secondDishId)
  }

  private onHalfAndHalfSelected = (firstDishId: string, secondDishId: string): void => {
    if (this.menu.halfAndHalfHasAddons(firstDishId, secondDishId)) {
      this.openAddonModalForHalves(firstDishId, secondDishId)
      return
    }
    this.addHalfAndHalf(firstDishId, secondDishId, [])
  }

  private navigateToOrderPage = (): void => {
    const basePath = window.location.pathname.replace(/\/$/, "")
    this.pjax.loadUrl(`${basePath}/order`)
  }

  private hasUnavailableItems = (): boolean =>
    this.basket.items.some((item) => !this.menu.isItemAvailable(item))

  private buildAddonsSubLines = (item: BasketItem): string =>
    this.menu.getItemAddons(item).map((addon) => {
      const amount = this.menu.getAddonLineAmount(addon)
      const quantityLabel = addon.quantity > 1 ? ` ×${addon.quantity}` : ""
      const priceLabel = amount > 0 ? ` ${this.menu.formatPrice(amount)}` : ""
      return `
        <div class="text-muted small ps-3 pt-1">+ ${addon.name}${quantityLabel}${priceLabel}</div>
      `
    }).join("")

  private render = (): void => {
    const hasItems = this.basket.items.length > 0
    const isStoreOpen = this.menuData.isStoreOpen !== false
    const canOrder = hasItems && !this.hasUnavailableItems() && isStoreOpen
    this.emptyEl?.classList.toggle("d-none", hasItems)

    for (const button of document.querySelectorAll("[data-basket-order]")) {
      const btn = button as HTMLButtonElement
      btn.disabled = !canOrder
      btn.onclick = canOrder ? this.navigateToOrderPage : null
    }

    if (!this.itemsEl) return

    let total = 0
    const rows: string[] = []
    const unavailableLabel = this.t("(Unavailable)")

    for (const item of this.basket.items) {
      if (!this.menu.itemExists(item)) continue

      const itemKey = this.menu.getItemKey(item)
      const lineTotal = this.menu.getItemLineTotal(item, null)
      total += lineTotal
      const minusIcon = item.count === 1 ? "bi-trash" : "bi-dash"
      const isUnavailable = !this.menu.isItemAvailable(item)
      const namePrefix = isUnavailable ? `${unavailableLabel} ` : ""

      const feesHtml = this.menu.getItemApplicableFees(item, null).map((fee) => {
        const amount = this.menu.getItemFeeLineAmount(item, fee, item.count)
        const label = fee.type === DishFeeType.Percentage ? `${fee.name} ${fee.percentage}%` : fee.name
        return `
        <div class="text-muted small ps-3 pt-1">+ ${label} ${this.menu.formatPrice(amount)}</div>
      `
      }).join("")

      const addonsHtml = this.buildAddonsSubLines(item)

      rows.push(`
        <div class="py-2 border-bottom${isUnavailable ? " opacity-50" : ""}">
          <div class="d-flex justify-content-between align-items-center">
            <div class="me-2">
              <div class="fw-semibold">${namePrefix}${this.menu.getItemName(item)}</div>
              <div class="text-muted small">${this.menu.formatPrice(this.menu.getItemUnitPrice(item))}</div>
            </div>
            <div class="d-flex align-items-center gap-2">
              <button type="button" class="btn btn-outline-secondary btn-sm" data-basket-dec data-item-key="${itemKey}" aria-label="decrease">
                <i class="bi ${minusIcon}"></i>
              </button>
              <span class="fw-semibold text-center" style="min-width: 1.5rem;">${item.count}</span>
              <button type="button" class="btn btn-outline-secondary btn-sm" data-basket-inc data-item-key="${itemKey}" aria-label="increase">
                <i class="bi bi-plus"></i>
              </button>
              <span class="fw-semibold text-nowrap text-end ms-2" style="min-width: 4rem;">${this.menu.formatPrice(lineTotal)}</span>
            </div>
          </div>
          ${feesHtml}
          ${addonsHtml}
        </div>
      `)
    }

    this.itemsEl.innerHTML = rows.join("")

    const totalText = hasItems ? `(${this.menu.formatPrice(total)})` : ""
    for (const totalEl of document.querySelectorAll("[data-basket-order-total]")) {
      totalEl.textContent = totalText
    }

    for (const button of this.itemsEl.querySelectorAll("[data-basket-dec]")) {
      button.addEventListener("click", () => this.changeCountByKey((button as HTMLElement).dataset.itemKey, -1))
    }
    for (const button of this.itemsEl.querySelectorAll("[data-basket-inc]")) {
      button.addEventListener("click", () => this.changeCountByKey((button as HTMLElement).dataset.itemKey, 1))
    }
  }
}
